import {
  Stack,
  Title,
  FlexSpacer,
  Button,
  IconButton,
  Alert,
  Tabs,
  DEFAULT_TEXT_HEIGHT,
  Skeleton
} from '@efast_public/fjd-component-library';
import Antragsdetails from './SchreibtischBody/Antragsdetails/Antragsdetails';
import classes from './Schreibtisch.module.css';
import EmptySchreibtischNoTabs from './EmptySchreibtischNoTabs';
import useTabs from '../../shared/hooks/useTabs';
import Antragsdokumente from './SchreibtischBody/Dokumente/Antragsdokumente';
import Kommunikation from './SchreibtischBody/Kommunikation/Kommunikation';
import { Bearbeitungsstand } from '../../api/models/Status/Bearbeitungsstand';
import { useStorageState } from 'react-storage-hooks';
import EmptySchreibtischNoAntraege from './EmptySchreibtischNoAntraege';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CaseDetailsModel } from '../../api/models/Case/CaseDetailsModel';
import { BehApiRoutes, behClient } from '../../api/beh-api';
import { base64ToBytes, openBlobInNewTab } from '../../shared/utils/fileUtils';
import { Bescheidstatus } from '../../api/models/Status/Bescheidstatus';
import _moment from 'moment';
import {useEffect, useState} from 'react';
import SwitchApplicationModals from '../../modals/BescheidModal/SwitchApplicationModals';
import AntragAbschliessenModal from '../../modals/AntragAbschliessen/AntragAbschliessenModal';
import DownloadModal from '../../modals/AntragDownload/AntragDownloadModal';
import NachrichtSendenModal from '../../modals/NachrichtSenden/NachrichtSendenModal';
import ZuweisungComponent from '../shared/Zuweisung/ZuweisungComponent';
import { useNotification } from '../../shared/notification/useNotification';
import { useProfile } from '../../shared/hooks/AuthContext';
import { RoutePaths } from '../AppRoutes';

export enum ContentTab {
  ANTRAGSDETAILS = 'antragsdetails',
  DOKUMENTE = 'dokumente',
  KOMMUNIKATION = 'kommunikation'
}

export const activeContentTabLocalStorageKey = 'beh-activeContentTab-state-';

const SchreibtischPage = () => {
  const [openSendenModal, setOpenSendenModal] = useState(false);
  const [openDecideApplicationModal, setOpenDecideApplicationModal] =
    useState(false);
  const [openAbschliessenModal, setOpenAbschliessenModal] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);

  const { notifyUserFromRequest } = useNotification();
  const navigate = useNavigate();
  const { empty, updateTab } = useTabs();
  const { caseId } = useParams() as { caseId: string };
  const { data: caseCount } = useQuery({
    queryKey: [BehApiRoutes.casesCount()]
  });

  if (!caseId) navigate(RoutePaths.ANTRAEGE);

  const {
    data: caseDetails,
    refetch: refetchCaseDetails,
    isLoading
  } = useQuery<CaseDetailsModel, Error>({
    queryKey: [BehApiRoutes.case(caseId ?? '')],
    enabled: !!caseId
  });

  useEffect(() => {
    if (caseDetails) {
      updateTab(caseDetails);
    }
  }, [caseDetails, updateTab]);

  const { refetch: fetchPdf } = useQuery<string>({
    queryKey: [BehApiRoutes.case(caseId ?? ''), 'application/pdf'],
    queryFn: () =>
      notifyUserFromRequest(
        () =>
          behClient.get(BehApiRoutes.case(caseId ?? ''), {
            headers: {
              Accept: 'application/pdf'
            }
          }),
        { loadingMessage: 'Lade PDF...' }
      ),
    enabled: false
  });
  const { profile } = useProfile();

  const bearbeitendeNutzer = caseDetails?.bearbeiterDto?.id === profile?.id;
  const canInteract =
    caseDetails?.bearbeitungsstand !== Bearbeitungsstand.ABGESCHLOSSEN;

  const [, setStorageActiveContentTabState] = useStorageState(
    localStorage,
    `${activeContentTabLocalStorageKey}${caseId}`
  );

  if (empty && caseCount === 0) {
    return <EmptySchreibtischNoAntraege />;
  }
  if (empty && caseCount !== 0) {
    return <EmptySchreibtischNoTabs />;
  }

  return (
    <>
      <Stack spacing={'xl'}>
        <Stack
          className={classes.caseNameTitle}
          id={'casename_title'}
          direction={'row'}
          spacing="xs"
        >
          {isLoading && <Skeleton width={300} height={DEFAULT_TEXT_HEIGHT} />}
          {caseDetails && !isLoading && (
            <Title level={2}>
              {`${caseDetails?.auftragsnummer} • ${caseDetails?.name}`}
            </Title>
          )}
          <FlexSpacer />
          <Stack direction="row" spacing="xs">
            <IconButton
              variant={'secondary-outline'}
              size={'md'}
              label={'Antrag herunterladen'}
              data-testid={'download_form'}
              icon={'download'}
              onClick={() => setOpenDownloadModal(true)}
              id={'download_btn'}
            />
            <IconButton
              variant={'secondary-outline'}
              size={'md'}
              label={'Antrag drucken'}
              data-testid={'print_formular'}
              icon={'printer'}
              onClick={() => {
                fetchPdf()
                  .then((pdfContent) => {
                    if (pdfContent.data) {
                      openBlobInNewTab(
                        new File(
                          [base64ToBytes(pdfContent.data)],
                          `${caseId}.pdf`,
                          { type: 'application/pdf' }
                        )
                      );
                    }
                  })
                  .catch(() => {});
              }}
              id={'print_formular'}
            />
          </Stack>
          {canInteract &&
            ((caseDetails?.bescheidstatus as Bescheidstatus) ===
              Bescheidstatus.POSITIV ||
            (caseDetails?.bescheidstatus as Bescheidstatus) ===
              Bescheidstatus.NEGATIV ? (
              <Button
                variant={'primary'}
                size={'md'}
                beforeIcon={'certificate-check'}
                data-testid={'abschliessen'}
                onClick={() => setOpenAbschliessenModal(true)}
                id={'bescheid_antragsdetails'}
                disabled={!bearbeitendeNutzer}
              >
                Abschließen
              </Button>
            ) : (
              <Button
                variant={'primary'}
                size={'md'}
                beforeIcon={'send'}
                data-testid={'bescheid'}
                onClick={() => setOpenDecideApplicationModal(true)}
                id={'bescheid_antragsdetails'}
                disabled={!bearbeitendeNutzer}
              >
                Bescheidversand
              </Button>
            ))}
          {canInteract && (
            <Button
              disabled={!caseDetails?.canCommunicate || !bearbeitendeNutzer}
              variant={'primary'}
              size={'md'}
              beforeIcon={'reply'}
              data-testid={'rueckfrage'}
              onClick={() => setOpenSendenModal(!openSendenModal)}
              id={'rueckfrage_antragsdetails'}
            >
              Rückfrage
            </Button>
          )}
        </Stack>
        {caseDetails?.bearbeitungsstand === Bearbeitungsstand.ABGESCHLOSSEN && (
          <div id={caseDetails.bearbeiterDto?.fullName}>
            <Stack
              id={caseDetails.bearbeiterDto?.fullName}
              className={classes.bearbeiter}
              direction="row"
            >
              <b>Bearbeiter:in: </b>&nbsp;{' '}
              {caseDetails.bearbeiterDto?.fullName ?? 'k. A.'}
            </Stack>
            <Alert
              variant={'warning'}
              message={`Abgeschlossene Anträge werden nach 30 Tagen aus dem Behörden-Client gelöscht. Dieser Antrag wurde am ${_moment.utc(caseDetails.abgeschlossenAm).local().format('DD.MM.YYYY, HH:mm')} von ${caseDetails.bearbeiterDto?.fullName} heruntergeladen und wird am ${_moment.utc(caseDetails.abgeschlossenAm).local().add(30, 'd').format('DD.MM.YYYY')} aus dem System gelöscht.`}
            />
          </div>
        )}
        {caseDetails?.bearbeitungsstand !== Bearbeitungsstand.ABGESCHLOSSEN &&
          caseDetails && (
            <Stack direction={'row'}>
              <FlexSpacer />
              <Stack direction="row">
                <ZuweisungComponent
                  antrag={caseDetails}
                  buttonSize="md"
                  isSchreibtisch
                />
              </Stack>
            </Stack>
          )}
        <div className={classes.antragsheader}>
          <Tabs id="antragsdetails-tabs">
            <Tabs.List>
              <Tabs.Tab label="Antragsdetails" icon={'document'} />
              <Tabs.Tab label="Dokumente" icon={'link'} />
              <Tabs.Tab label="Kommunikation" icon={'email'} />
            </Tabs.List>
            <Tabs.Panels>
              <Tabs.Panel padding={'m'}>
                <Antragsdetails />
              </Tabs.Panel>
              <Tabs.Panel padding={'m'}>
                <Antragsdokumente />
              </Tabs.Panel>
              <Tabs.Panel padding={'m'}>
                <Kommunikation
                  setContentTabState={setStorageActiveContentTabState}
                  openAbschliessenModal={() => setOpenAbschliessenModal(true)}
                />
              </Tabs.Panel>
            </Tabs.Panels>
          </Tabs>
        </div>
      </Stack>
      {canInteract && openDecideApplicationModal && caseDetails && (
        <SwitchApplicationModals
          onClose={() => {
            setOpenDecideApplicationModal(false);
          }}
          organisationUnitName={caseDetails?.organizationalUnitName}
          caseId={caseDetails?.caseId}
          setContentTabState={setStorageActiveContentTabState}
          openAbschliessenModal={() => setOpenAbschliessenModal(true)}
        />
      )}
      {caseDetails?.isBeschieden && caseDetails && (
        <AntragAbschliessenModal
          onClose={() => {
            setOpenAbschliessenModal(false);
            refetchCaseDetails();
          }}
          auftragsnummer={caseDetails?.auftragsnummer}
          caseId={caseDetails?.caseId}
          version={caseDetails?.version}
          isOpen={openAbschliessenModal}
        />
      )}
      {openDownloadModal && caseDetails && (
        <DownloadModal
          onClose={() => {
            setOpenDownloadModal(false);
          }}
          auftragsnummer={caseDetails?.auftragsnummer}
          caseId={caseDetails?.caseId}
          isOpen
        />
      )}
      {caseDetails && canInteract && openSendenModal && (
        <NachrichtSendenModal
          onClose={() => {
            setOpenSendenModal(false);
            refetchCaseDetails();
          }}
          caseId={caseDetails?.caseId}
          caseDetails={caseDetails}
          caseVersion={caseDetails?.version}
          isOpen
        />
      )}
    </>
  );
};

export default SchreibtischPage;
